import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { ACCOUNT_LIMITED_MESSAGE } from '@scio3/graphql-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@scio3/ui';
import { useDunningBlockStore } from '@/stores/dunning-block-store';
import { useDunningState } from '@/hooks/useDunningState';
import { useDunningContactLine } from '@/hooks/useDunningContactLine';
import { useAuthStore } from '@/stores/auth-store';
import { isContentCreationBlocked } from '@/lib/dunning-content-blocked';

/** Bolded inside `dunning.contact.support`; not a translatable string. */
const SUPPORT_EMAIL = 'support@optisigns.com';

/**
 * Drop a caller's toast that repeats this dialog. On ACCOUNT_LIMITED the GraphQL client
 * rewrites the error to ACCOUNT_LIMITED_MESSAGE so a component doing
 * `toast.error(err.message)` still reports a failure, never a silent "Saved"; with the
 * dialog open that toast is the same refusal said twice, so it goes. Matched on the exact
 * sentence, so a caller's own copy ("Failed to save asset") and every other toast stay.
 */
function dismissAccountLimitedToasts(): void {
  for (const item of toast.getToasts()) {
    if ('title' in item && item.title === ACCOUNT_LIMITED_MESSAGE) toast.dismiss(item.id);
  }
}

/** The caller's toast lands after the event (its catch runs later): sweep after it. */
const TOAST_SWEEP_DELAYS_MS = [0, 300];

/**
 * "New content is paused" — shown when a limited account tries to CREATE new content
 * (a design, a smallapp, a widget).
 *
 * The counterpart to the ladder's "limit account changes" rung for the one write
 * open-api's guard cannot reach: the Designer saves through its own service, so
 * the only place to stop a NEW design is before its window opens. Blocking
 * creation (not edits of existing content) is a deliberately lighter, more
 * accurate reading of the diagram's "no new publishing".
 *
 * Same voice as the banner and the login popup, one CTA straight to billing, and a
 * plain dismiss — it never traps anyone, and every escape hatch (billing, export,
 * cancel) stays open.
 *
 * It is ALSO what a customer sees when the server refuses any other write with
 * ACCOUNT_LIMITED (pairing a screen, inviting a user, publishing). The GraphQL
 * client dispatches `auth:account-limited` for that, and nothing listened: most
 * callers show their own fixed copy ("Failed to save asset"), so a blocked write
 * read as a generic failure with no mention of billing and no way to pay. Opening
 * this dialog from the event gives every blocked write the same explanation and
 * the same pay button, whatever the calling component does with the error.
 */
export function DunningCreateBlockedDialog() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOpen = useDunningBlockStore((s) => s.isOpen);
  const close = useDunningBlockStore((s) => s.close);
  const reason = useDunningBlockStore((s) => s.reason);
  const open = useDunningBlockStore((s) => s.open);
  const { payPath, canPay } = useDunningState();
  const contactLine = useDunningContactLine();
  const me = useAuthStore((s) => s.me);
  const blocked = isContentCreationBlocked(me);

  // The server refused a write because the account is limited. One message for one
  // refusal: this dialog, not this dialog plus the caller's toast of the same sentence.
  useEffect(() => {
    const timers = new Set<number>();
    const onLimited = () => {
      open('write');
      for (const ms of TOAST_SWEEP_DELAYS_MS) {
        const id = window.setTimeout(() => {
          timers.delete(id);
          dismissAccountLimitedToasts();
        }, ms);
        timers.add(id);
      }
    };
    window.addEventListener('auth:account-limited', onLimited);
    return () => {
      window.removeEventListener('auth:account-limited', onLimited);
      for (const id of timers) window.clearTimeout(id);
    };
  }, [open]);

  // They paid while the dialog was up (`me` refetched): it no longer applies.
  //
  // Only on a real blocked → clear TRANSITION. The server can refuse a write before
  // this tab's `me` has caught up with the account becoming limited, and closing
  // whenever `blocked` reads false would shut the dialog the instant it opened.
  const wasBlocked = useRef(blocked);
  useEffect(() => {
    if (isOpen && wasBlocked.current && !blocked) close();
    wasBlocked.current = blocked;
  }, [isOpen, blocked, close]);

  const handlePay = useCallback(() => {
    close();
    navigate(payPath);
  }, [close, navigate, payPath]);

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) close();
    },
    [close],
  );

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md" data-testid="dunning-create-blocked-dialog">
        <DialogHeader>
          <DialogTitle>
            {reason === 'write'
              ? t('dunning.writeBlocked.title')
              : t('dunning.createBlocked.title')}
          </DialogTitle>
          {/* What is paused and how to undo it read as one paragraph: the
              reassurance follows the body on the same line. */}
          <DialogDescription>
            {reason === 'write'
              ? `${t('dunning.banner.limited')} ${t('dunning.writeBlocked.reassurance')}`
              : `${t('dunning.createBlocked.body')} ${t('dunning.createBlocked.reassurance')}`}
          </DialogDescription>
        </DialogHeader>

        {/* The contact line gets its own paragraph, with the support address in
            bold so it is the thing the eye lands on. The address is not
            translated, so it is found in the translated line and wrapped, the
            same way the banners bold their dates. */}
        {contactLine && (
          <p className="text-sm text-muted-foreground" data-testid="dunning-contact-line">
            {contactLine.includes(SUPPORT_EMAIL) ? (
              <>
                {contactLine.slice(0, contactLine.indexOf(SUPPORT_EMAIL))}
                <strong className="font-semibold text-foreground">{SUPPORT_EMAIL}</strong>
                {contactLine.slice(contactLine.indexOf(SUPPORT_EMAIL) + SUPPORT_EMAIL.length)}
              </>
            ) : (
              contactLine
            )}
          </p>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={close} data-testid="dunning-create-blocked-dismiss-btn">
            {t('dunning.createBlocked.dismiss')}
          </Button>
          {/* Only for a viewer who can open billing; everyone else already has the
              contact line above telling them who can. */}
          {canPay && (
            <Button onClick={handlePay} data-testid="dunning-create-blocked-pay-btn">
              {t('dunning.cta.viewInvoice')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
